import React, { Component, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { ApartmentOutlined } from "@ant-design/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAsterisk, faClose, faDeleteLeft, faEdit, faLock, faTrash } from "@fortawesome/free-solid-svg-icons";
import { Popover, Tooltip } from "antd";
import { addFieldToBranchSchema, removeFieldFromBranchSchema, resetAndReorderBranchSchema, resetbranchSchemaNewFields, setPropertyToBeRemoveFromSchema } from "../../middleware/redux/reducers/branch.reducer";
import { setNotification } from "../../middleware/redux/reducers/notification.reducer";
import { useDispatch } from "react-redux";

// a little function to help us with reordering the result
const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

// Flattens a root-with-nested-`.children` list into submit order: each item immediately
// followed by its own descendants, recursively — how a Field-Flow-linked block of fields
// (a trigger + everything it locks under itself) gets sequential `order` values in one go.
const flattenTree = (list) =>
  (list ?? []).flatMap((item) => [item, ...flattenTree(item.children)]);

// One row — a field's label + its mandatory/remove controls. Used for both the draggable
// root row and (recursively) its locked, non-draggable children.
function FieldRow({ item, editColumn, handelAddBranches, onToggleMandatory, onRemove, dispatch, depth = 0, parentLabel, dragHandleProps }) {
  const isLocked = depth > 0;

  return (
    <div className="icon-wrapper">
      <div
        className="edit-form-input-control input inputItemList"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          direction: 'ltr',
          alignItems: 'center',
          marginLeft: depth * 28,
        }}
        {...dragHandleProps}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {isLocked && (
            <Tooltip title={`This field moves together with "${parentLabel}" — it's revealed by that field's condition`}>
              <FontAwesomeIcon
                icon={faLock}
                style={{ fontSize: 12, width: 14, color: '#7c98b6', cursor: 'pointer' }}
                onMouseDown={() => dispatch(setNotification({
                  notificationState: true,
                  error: false,
                  placement: 'bottomRight',
                  message: `This field moves together with "${parentLabel}".`,
                }))}
              />
            </Tooltip>
          )}
          <span className="draggeableTable-text">{item.label}</span>
        </span>

        {editColumn ? (
          <FontAwesomeIcon
            icon={faClose}
            style={{ cursor: 'pointer' }}
            onClick={handelAddBranches ? () => handelAddBranches(item._id) : async () => {
              await dispatch(setPropertyToBeRemoveFromSchema(item._id));
            }}
          />
        ) : (
          <span style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {item?.isReadOnly ? (
              <Tooltip title="Required field — always mandatory">
                <FontAwesomeIcon
                  icon={faAsterisk}
                  style={{ fontSize: '13px', width: '16px', color: '#ff4d4f', cursor: 'not-allowed' }}
                />
              </Tooltip>
            ) : (
              <FontAwesomeIcon
                title="Required"
                icon={faAsterisk}
                style={{ fontSize: '13px', width: '16px', cursor: 'pointer', color: item?.isMandatory ? '#ff4d4f' : '#c0c0c0' }}
                onClick={onToggleMandatory ? () => onToggleMandatory(item) : () => dispatch(addFieldToBranchSchema({ _id: item._id, isMandatory: item?.isMandatory ? false : true }))}
              />
            )}
            {item?.isReadOnly ? (
              <Tooltip title="Required field — can't be removed">
                <FontAwesomeIcon icon={faClose} style={{ fontSize: '16px', width: '20px', color: '#c0c0c0', cursor: 'not-allowed' }} />
              </Tooltip>
            ) : (
              <FontAwesomeIcon
                icon={faClose}
                style={{ fontSize: '16px', width: '20px', cursor: 'pointer', color: '#595959' }}
                onClick={onRemove ? () => onRemove(item._id) : async () => {
                  await dispatch(setPropertyToBeRemoveFromSchema(item._id));
                }}
              />
            )}
          </span>
        )}
      </div>

      {(item.children ?? []).map((child) => (
        <FieldRow
          key={child._id}
          item={child}
          editColumn={editColumn}
          handelAddBranches={handelAddBranches}
          onToggleMandatory={onToggleMandatory}
          onRemove={onRemove}
          dispatch={dispatch}
          depth={depth + 1}
          parentLabel={item.label}
        />
      ))}
    </div>
  );
}

const DraggableList = ({list, editColumn, handelAddBranches, updateUserBranchView, reorderSchema, objectRefetch, onToggleMandatory, onRemove}) => {


  const [items, setItems] = useState(list)
  useEffect(()=>{
    setItems(list?.filter((l)=>l.isLocalDeleted!=1));
  }, [list]);

  useEffect(()=>{

    // we are calling this function to update the prop order in redux as well
    sessionStorage.setItem('reorderedItems',
      JSON.stringify(items)
    );
  }, [items]);


  const onDragEnd = async (result) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }


    const reordered = reorder(
      items,
      result.source.index,
      result.destination.index
    );

    setItems(reordered);


    if(updateUserBranchView){

      await updateUserBranchView(flattenTree(reordered)?.map((prop)=>prop._id));
    }else{
      if(reorderSchema){
        await reorderSchema({
          variables:{
            input:{fields: flattenTree(reordered).map((item)=>({propertyId: item._id}))}
          }
        });
      }
      if(objectRefetch){
        await objectRefetch();
      }
    }


  }


  // Normally you would want to split things out into separate components.
  // But in this example everything is just done in one place for simplicity
  const dispatch = useDispatch();
    return (
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable">
          {(provided, snapshot) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
            >
              {items?.map((item, index) => (
                item?.isLocalDeleted ? null :
                <Draggable key={item._id} draggableId={item._id} index={index}

                >
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      style={{ opacity: snapshot.isDragging ? 0.5 : 1, ...provided.draggableProps.style }}
                    >
                      <FieldRow
                        item={item}
                        editColumn={editColumn}
                        handelAddBranches={handelAddBranches}
                        onToggleMandatory={onToggleMandatory}
                        onRemove={onRemove}
                        dispatch={dispatch}
                        dragHandleProps={provided.dragHandleProps}
                      />
                    </div>

                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

    );
}

export default DraggableList;
